/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-nested-ternary */
import { useEffect, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import _ from 'lodash';
import * as Sentry from '@sentry/react';
import {
    Dialog,
    Box,
    Button,
    Stack,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Chip,
    Tooltip,
    CircularProgress,
    Alert,
    ToggleButtonGroup,
    ToggleButton,
    Autocomplete,
    TextField,
    Divider,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Transition } from '.';
import Iconify from '../Iconify';
import { AssignUserAvatar } from '../custom-avatar';
import { useSelector, dispatch } from '../../redux/store';
import { fetchBulkLeadContext, smartBulkCreateReminders, selectLeads } from '../../redux/slices/lead';
import { buildPreviewRows, computeSpreadWindow, skipSunday, TIMING_MODES, detectTimingMode } from '../../utils/smartReminderRules';
import useCustomSnackbar from '../../hooks/useCustomSnackbar';
import {
    reminderDialogStyle,
    reminderStackStyle,
    reminderFooterStyle,
    cancelButtonStyle,
    saveButtonStyle,
} from '../../styles/AddReminderPopup';

const METHOD_ICONS = {
    CALL: 'fluent:call-20-filled',
    EMAIL: 'eva:email-fill',
    SMS: 'bi:chat-left-dots-fill',
    WHATSAPP: 'ri:whatsapp-fill',
    PAYMENT: 'mdi:credit-card-clock-outline',
    OTHER: 'mdi:target',
};

// Channel-specific brand colours (WhatsApp green / Payment orange) intentionally
// stay as raw hex — they're brand tokens, not theme tokens. The semantic ones
// (Call/Email/SMS/Other) resolve to palette values where possible.
const METHOD_COLORS = {
    CALL: 'success.main',
    EMAIL: 'info.main',
    SMS: '#7635DC', // role-purple, see ROLE_COLORS in src/styles/PracticeUserListStyle.js
    WHATSAPP: '#25D366', // WhatsApp brand green — keep
    PAYMENT: '#FF6C40', // payment-orange brand accent — keep
    OTHER: 'text.secondary',
};

const smartDialogStyle = {
    ...reminderDialogStyle,
    ' .MuiDialog-container': { width: '1300px', height: 'auto', maxHeight: '92vh', maxWidth: '96vw' },
    ' .MuiPaper-root': { width: '100%', maxWidth: '96vw', maxHeight: '92vh', boxShadow: '0px 24px 48px rgba(0, 0, 0, 0.12)', margin: 0, paddingBottom: '75px', borderRadius: '16px', overflow: 'hidden' },
};

const TIMING_MODE_LABELS = {
    [TIMING_MODES.CATCH_UP]: { label: 'Catch-up Spread', icon: 'mdi:calendar-clock', desc: 'Spread evenly across days by priority' },
    [TIMING_MODES.FOLLOW_UP_SCHEDULE]: { label: 'Follow-up Schedule', icon: 'mdi:calendar-check', desc: 'Based on consultation date intervals' },
    [TIMING_MODES.NEW_ENQUIRY]: { label: 'New Enquiry', icon: 'mdi:phone-plus', desc: 'Call schedule from day of enquiry (15/day)' },
};

const SmartBulkReminderModal = ({ open, onClose, onSuccess }) => {
    const { enqueueSnackbar } = useCustomSnackbar();
    const { multiLead } = useSelector((state) => state.lead);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { practiceUser } = useSelector((state) => state.practiceUser);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [rawLeadContext, setRawLeadContext] = useState([]);
    const [previewRows, setPreviewRows] = useState([]);
    const [creating, setCreating] = useState(false);
    const [timingMode, setTimingMode] = useState(TIMING_MODES.CATCH_UP);
    const [autoDetected, setAutoDetected] = useState(null);
    const [assigneeOverride, setAssigneeOverride] = useState([]);
    const [perRowAssignees, setPerRowAssignees] = useState({}); // leadId → array of practiceUser objects
    const [dayOffset, setDayOffset] = useState(null);
    const [leadsPerDay, setLeadsPerDay] = useState(15);

    // Build sorted user list for the assignee override dropdown
    const userOptions = useMemo(() => {
        const users = practiceUser?.results?.filter((el) => el.user && el.user.first_name && !el.user.is_superuser) || [];
        return _.orderBy(users, [(o) => o.user?.first_name?.toLowerCase()], ['asc']);
    }, [practiceUser]);

    const leadIds = multiLead?.leadId || [];

    // Fetch lead context when modal opens, then detect mode from the FULL set
    // returned by the server. We avoid running detection on `selectedRows`
    // because the DataGrid uses `keepNonExistentRowsSelected` — visible rows
    // only cover the current page, so cross-page selections would be missed
    // (booked-lead block would silently not fire).
    useEffect(() => {
        if (open && leadIds.length > 0 && practiceId) {
            setPerRowAssignees({});
            setAutoDetected(null);
            setLoading(true);
            setError('');
            dispatch(fetchBulkLeadContext(practiceId, leadIds))
                .then((result) => {
                    try {
                        if (result?.success) {
                            setRawLeadContext(result.data);
                            const detection = detectTimingMode(result.data);
                            setAutoDetected(detection);
                            const effectiveMode = detection.mode ?? TIMING_MODES.CATCH_UP;
                            setTimingMode(effectiveMode);
                            const rows = buildPreviewRows(result.data, effectiveMode, leadsPerDay);
                            setPreviewRows(rows);
                        } else {
                            setError(typeof result?.error === 'string' ? result.error : 'Failed to load lead context');
                        }
                    } catch (err) {
                        // Defensive: anything thrown by buildPreviewRows / detectTimingMode would
                        // otherwise leave the spinner forever. Surface to Sentry + the user.
                        Sentry.captureException(err, { tags: { component: 'SmartBulkReminderModal' } });
                        setError('Could not build the preview. Please try again or refresh the page.');
                        enqueueSnackbar('Could not build the preview. Please try again or refresh the page.', {
                            variant: 'error',
                            anchorOrigin: { vertical: 'top', horizontal: 'center' },
                        });
                    }
                })
                .catch((err) => {
                    Sentry.captureException(err, { tags: { component: 'SmartBulkReminderModal' } });
                    setError('Failed to load lead context');
                    enqueueSnackbar('Failed to load lead context', {
                        variant: 'error',
                        anchorOrigin: { vertical: 'top', horizontal: 'center' },
                    });
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [open]);

    // Apply day offset to computed preview rows — only when user has explicitly selected a chip
    const applyOffset = useCallback((rows, offset) => {
        if (offset === null || offset === undefined) return rows;
        if (offset === 0) {
            // "Today" chip: move overdue dates to today, leave future dates alone
            const today = moment().startOf('day');
            let staggerSlot = 0;
            return rows.map((r) => {
                if (r.isOverdue && r.dueDate && r.dueDate.isValid()) {
                    const dueDate = skipSunday(today.clone().hour(9).minute(staggerSlot * 15));
                    staggerSlot += 1;
                    return { ...r, dueDate, isOverdue: false };
                }
                return r;
            });
        }
        return rows.map((r) => ({
            ...r,
            dueDate: r.dueDate && r.dueDate.isValid() ? skipSunday(r.dueDate.clone().add(offset, 'days')) : r.dueDate,
        }));
    }, []);

    // Recompute preview rows when timing mode changes
    const handleTimingModeChange = useCallback((_, newMode) => {
        if (!newMode) return; // prevent deselect
        setTimingMode(newMode);
        setDayOffset(null); // reset — no adjustment until user selects a chip
        if (rawLeadContext.length > 0) {
            const rows = buildPreviewRows(rawLeadContext, newMode, leadsPerDay);
            setPreviewRows(rows);
        }
    }, [rawLeadContext, leadsPerDay]);

    // Handle day offset change — clicking same chip clears to null (no adjustment)
    const handleDayOffsetChange = useCallback((newOffset) => {
        const resolvedOffset = dayOffset === newOffset ? null : newOffset;
        setDayOffset(resolvedOffset);
        if (rawLeadContext.length > 0) {
            const baseRows = buildPreviewRows(rawLeadContext, timingMode, leadsPerDay);
            setPreviewRows(applyOffset(baseRows, resolvedOffset));
        }
    }, [rawLeadContext, timingMode, leadsPerDay, applyOffset, dayOffset]);

    // Recompute when leads-per-day changes
    const handleLeadsPerDayChange = useCallback((val) => {
        const num = Math.max(1, Math.min(50, parseInt(val, 10) || 15));
        setLeadsPerDay(num);
        if (rawLeadContext.length > 0) {
            const baseRows = buildPreviewRows(rawLeadContext, timingMode, num);
            setPreviewRows(applyOffset(baseRows, dayOffset));
        }
    }, [rawLeadContext, timingMode, applyOffset, dayOffset]);

    // Days since last contact — null/no-contact ranks as the most urgent (Infinity).
    const daysSinceContact = useCallback((row) => {
        if (!row.latestNoteDate || !row.latestNoteDate.isValid()) return Infinity;
        return moment().startOf('day').diff(row.latestNoteDate.clone().startOf('day'), 'days');
    }, []);

    const recencyBorderColor = useCallback((row) => {
        if (row.isArchiveCandidate) return 'transparent';
        const days = daysSinceContact(row);
        if (days === Infinity) return 'error.main';      // never contacted — red
        if (days > 30) return 'error.light';             // cold — faded red
        if (days > 7) return 'warning.main';             // stale — amber
        return 'success.main';                            // fresh — green
    }, [daysSinceContact]);

    // Sort for display: oldest contact first (most urgent), archive candidates last.
    const displayedRows = useMemo(() => (
        [...previewRows].sort((a, b) => {
            if (a.isArchiveCandidate && !b.isArchiveCandidate) return 1;
            if (!a.isArchiveCandidate && b.isArchiveCandidate) return -1;
            return daysSinceContact(b) - daysSinceContact(a); // most days first
        })
    ), [previewRows, daysSinceContact]);

    // Resolve who a row is assigned to: per-row override > bulk override > lead default.
    const resolveAssignees = useCallback((row) => {
        if (perRowAssignees[row.leadId]) return perRowAssignees[row.leadId];
        if (assigneeOverride.length > 0) return assigneeOverride;
        return row.assignees || [];
    }, [perRowAssignees, assigneeOverride]);

    const checkedRows = useMemo(() => previewRows.filter((r) => r.checked), [previewRows]);
    const archiveCandidates = useMemo(() => previewRows.filter((r) => r.isArchiveCandidate), [previewRows]);
    const overdueRows = useMemo(() => previewRows.filter((r) => r.isOverdue), [previewRows]);
    const leadsWithReminders = useMemo(() => previewRows.filter((r) => r.pendingReminderCount > 0), [previewRows]);
    const spreadDays = useMemo(() => computeSpreadWindow(checkedRows.length), [checkedRows.length]);
    const appointmentWarnings = useMemo(() => checkedRows.filter((r) => r.hasUpcomingAppointment).length, [checkedRows]);

    const toggleRow = (leadId) => {
        setPreviewRows((prev) =>
            prev.map((r) => (r.leadId === leadId ? { ...r, checked: !r.checked } : r))
        );
    };

    const updateRowDate = (leadId, newDate) => {
        if (!newDate || !newDate.isValid()) return;
        setPreviewRows((prev) =>
            prev.map((r) => (r.leadId === leadId ? { ...r, dueDate: newDate, isOverdue: false } : r))
        );
    };

    const toggleAll = (checked) => {
        setPreviewRows((prev) => prev.map((r) => ({ ...r, checked })));
    };

    const handleCreate = () => {
        // Filter out archive candidates (no dueDate) even if somehow checked
        const toCreate = checkedRows.filter((r) => r.dueDate && r.dueDate.isValid && r.dueDate.isValid());
        if (toCreate.length === 0) return;

        // Guard: every reminder must have at least one assignee, otherwise the
        // backend will silently create an unassigned reminder no-one owns.
        const rowsMissingAssignees = toCreate.filter((row) => resolveAssignees(row).length === 0);
        if (rowsMissingAssignees.length > 0) {
            const names = rowsMissingAssignees
                .slice(0, 5)
                .map((r) => `${r.firstName || ''} ${r.lastName || ''}`.trim() || `Lead ${r.leadId}`)
                .join(', ');
            const more = rowsMissingAssignees.length > 5 ? ` (+${rowsMissingAssignees.length - 5} more)` : '';
            enqueueSnackbar(
                `Cannot create reminders — ${rowsMissingAssignees.length} lead${rowsMissingAssignees.length > 1 ? 's have' : ' has'} no assignee: ${names}${more}`,
                {
                    variant: 'error',
                    anchorOrigin: { vertical: 'top', horizontal: 'center' },
                }
            );
            return;
        }

        setCreating(true);

        const reminders = toCreate.map((row) => ({
            lead_id: row.leadId,
            datetime: row.dueDate.toISOString(),
            method: row.method,
            title: row.title,
            note: row.note,
            group: row.group,
            priority: row.priority,
            assignees: resolveAssignees(row).map((a) => a.id),
        }));

        const count = reminders.length;

        // Close modal immediately
        onClose();
        enqueueSnackbar(`Creating ${count} smart reminders...`, {
            variant: 'info',
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
        });

        dispatch(
            smartBulkCreateReminders(practiceId, reminders, (result) => {
                setCreating(false);
                if (result.success) {
                    // Clear selection only on success — failed runs preserve the
                    // user's selection so they can retry without re-selecting.
                    dispatch(selectLeads({ leadId: [], type: '', loading: false }));
                    enqueueSnackbar(`${result.data?.length || count} reminders created successfully`, {
                        variant: 'success',
                        anchorOrigin: { vertical: 'top', horizontal: 'center' },
                    });
                    onSuccess?.();
                } else {
                    const errMsg = typeof result.error === 'object' ? JSON.stringify(result.error) : result.error || 'Failed to create reminders';
                    enqueueSnackbar(errMsg, {
                        variant: 'error',
                        anchorOrigin: { vertical: 'top', horizontal: 'center' },
                    });
                }
            })
        );
    };

    const allChecked = previewRows.length > 0 && previewRows.every((r) => r.checked);
    const someChecked = previewRows.some((r) => r.checked) && !allChecked;

    const isBlocked = autoDetected?.block === true;

    // Modes available to the user. Selection is single-status per tab,
    // so detection narrows the picker to only what fits — Catch-up is
    // always offered as a universal fallback.
    const availableModes = useMemo(() => {
        const detected = autoDetected?.mode;
        if (detected === TIMING_MODES.FOLLOW_UP_SCHEDULE) return [TIMING_MODES.FOLLOW_UP_SCHEDULE, TIMING_MODES.CATCH_UP];
        if (detected === TIMING_MODES.NEW_ENQUIRY) return [TIMING_MODES.NEW_ENQUIRY, TIMING_MODES.CATCH_UP];
        return [TIMING_MODES.CATCH_UP];
    }, [autoDetected]);

    const modeHeaderText = useMemo(() => {
        if (timingMode === TIMING_MODES.FOLLOW_UP_SCHEDULE) {
            return `Post-consultation follow-ups — ${checkedRows.length} reminders over ~5 months`;
        }
        if (timingMode === TIMING_MODES.NEW_ENQUIRY) {
            return `New enquiry drip — ${checkedRows.length} reminders over ~30 days`;
        }
        return `Catch-up spread — ${checkedRows.length} reminders over the next ${spreadDays} day${spreadDays !== 1 ? 's' : ''}`;
    }, [timingMode, checkedRows.length, spreadDays]);

    // Subtitle text depends on timing mode
    const subtitleText = useMemo(() => {
        if (timingMode === TIMING_MODES.FOLLOW_UP_SCHEDULE) {
            const withConsDate = checkedRows.filter((r) => r.consultationAttendedDate);
            return `${checkedRows.length} of ${previewRows.length} leads selected · ${withConsDate.length} with consultation date`;
        }
        if (timingMode === TIMING_MODES.NEW_ENQUIRY) {
            const totalDays = Math.ceil(checkedRows.length / leadsPerDay);
            return `${checkedRows.length} of ${previewRows.length} leads selected · ${leadsPerDay}/day across ${totalDays} day${totalDays > 1 ? 's' : ''} · Priority sorted`;
        }
        return `${checkedRows.length} of ${previewRows.length} leads selected · Spread across ${spreadDays} day${spreadDays > 1 ? 's' : ''} · Starting today`;
    }, [timingMode, checkedRows, previewRows, spreadDays, leadsPerDay]);

    return (
        <Dialog TransitionComponent={Transition} open={open} onClose={onClose} sx={smartDialogStyle}>
            <Stack sx={{ ...reminderStackStyle, p: 2.5 }}>
                {/* Header */}
                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Smart Bulk Reminders
                    </Typography>
                    {!loading && !isBlocked && previewRows.length > 0 && (
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mt: 0.25 }}>
                            {modeHeaderText}
                        </Typography>
                    )}
                    {!loading && previewRows.length > 0 && (
                        <Typography variant="caption" color="text.secondary" component="div">
                            {subtitleText}
                        </Typography>
                    )}
                </Box>

                {/* Status chips — own row, only render what applies */}
                {!loading && previewRows.length > 0 && (appointmentWarnings > 0 || (overdueRows.length > 0 && (timingMode === TIMING_MODES.FOLLOW_UP_SCHEDULE || timingMode === TIMING_MODES.NEW_ENQUIRY)) || (archiveCandidates.length > 0 && (timingMode === TIMING_MODES.FOLLOW_UP_SCHEDULE || timingMode === TIMING_MODES.NEW_ENQUIRY)) || leadsWithReminders.length > 0) && (
                    <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                        {appointmentWarnings > 0 && (
                            <Chip
                                label={`${appointmentWarnings} with upcoming appt`}
                                size="small"
                                color="warning"
                                variant="outlined"
                            />
                        )}
                        {overdueRows.length > 0 && (timingMode === TIMING_MODES.FOLLOW_UP_SCHEDULE || timingMode === TIMING_MODES.NEW_ENQUIRY) && (
                            <Chip
                                label={`${overdueRows.length} overdue — use Shift to reschedule`}
                                size="small"
                                color="error"
                                variant="outlined"
                                icon={<Iconify icon="mdi:clock-alert-outline" />}
                            />
                        )}
                        {archiveCandidates.length > 0 && (timingMode === TIMING_MODES.FOLLOW_UP_SCHEDULE || timingMode === TIMING_MODES.NEW_ENQUIRY) && (
                            <Chip
                                label={`${archiveCandidates.length} archive candidate${archiveCandidates.length > 1 ? 's' : ''}`}
                                size="small"
                                variant="outlined"
                                icon={<Iconify icon="mdi:archive-outline" />}
                            />
                        )}
                        {leadsWithReminders.length > 0 && (
                            <Chip
                                label={`${leadsWithReminders.length} already have reminders (unchecked)`}
                                size="small"
                                color="warning"
                                variant="outlined"
                                icon={<Iconify icon="mdi:bell-alert" />}
                            />
                        )}
                    </Stack>
                )}

                {/* Blocked warning — compact inline */}
                {isBlocked && autoDetected?.bookedCount > 0 && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{
                            mb: 1.5,
                            px: 1.25,
                            py: 0.75,
                            borderRadius: 1,
                            bgcolor: 'warning.lighter',
                            border: '1px solid',
                            borderColor: 'warning.light',
                        }}
                    >
                        <Iconify icon="mdi:alert-circle-outline" sx={{ width: 16, height: 16, color: 'warning.dark', flexShrink: 0 }} />
                        <Typography variant="caption" sx={{ color: 'warning.dark', fontSize: '0.75rem' }}>
                            {autoDetected.bookedCount} already booked — remove to continue.
                        </Typography>
                    </Stack>
                )}

                {/* Toolbar — assignee + schedule + shift + per-day in one polished strip */}
                {!loading && previewRows.length > 0 && (
                    <Box
                        sx={{
                            mb: 2,
                            px: 2,
                            py: 1.25,
                            borderRadius: 1.5,
                            bgcolor: 'background.neutral',
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                            divider={<Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />}
                            sx={{ flexWrap: 'wrap', rowGap: 1 }}
                        >
                            {/* Assign */}
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 260 }}>
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.65rem', letterSpacing: 0.6, lineHeight: 1, whiteSpace: 'nowrap', fontWeight: 600 }}
                                >
                                    Assign to
                                </Typography>
                                <Autocomplete
                                    multiple
                                    size="small"
                                    options={userOptions}
                                    value={assigneeOverride}
                                    onChange={(_, newVal) => setAssigneeOverride(newVal)}
                                    getOptionLabel={(opt) => `${opt.user?.first_name || ''} ${opt.user?.last_name || ''}`.trim()}
                                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                                    renderOption={(props, opt) => (
                                        <li {...props} key={opt.id}>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <AssignUserAvatar
                                                    name={`${opt.user?.first_name || ''} ${opt.user?.last_name || ''}`}
                                                    src={opt.user?.avatar || ''}
                                                    role={opt.role}
                                                    sx={{ width: 24, height: 24, fontSize: '0.7rem' }}
                                                />
                                                <Typography variant="body2">
                                                    {opt.user?.first_name} {opt.user?.last_name}
                                                </Typography>
                                                <Chip
                                                    label={opt.role || 'USER'}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        height: 18,
                                                        fontSize: '0.6rem',
                                                        // Inline role-tint kept for the per-row chip — the canonical
                                                        // ROLE_COLORS map (PracticeUserListStyle.js) uses a different
                                                        // colour scheme (admin red on light orange) that doesn't fit
                                                        // here. The purple/green/grey trio matches existing tag UI
                                                        // elsewhere in the app.
                                                        color: opt.role === 'ADMIN' ? '#7635DC' : opt.role === 'TCO' ? 'success.main' : 'text.secondary',
                                                        borderColor: opt.role === 'ADMIN' ? '#7635DC44' : opt.role === 'TCO' ? 'success.light' : 'divider',
                                                    }}
                                                />
                                            </Stack>
                                        </li>
                                    )}
                                    renderTags={(selected, getTagProps) =>
                                        selected.map((opt, index) => (
                                            <Chip
                                                {...getTagProps({ index })}
                                                key={opt.id}
                                                size="small"
                                                label={`${opt.user?.first_name || ''} ${opt.user?.last_name?.[0] || ''}.`}
                                                avatar={
                                                    <AssignUserAvatar
                                                        name={`${opt.user?.first_name || ''} ${opt.user?.last_name || ''}`}
                                                        src={opt.user?.avatar || ''}
                                                        role={opt.role}
                                                        sx={{ width: 20, height: 20, fontSize: '0.6rem' }}
                                                    />
                                                }
                                            />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder={assigneeOverride.length === 0 ? 'Per-lead default' : ''}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    bgcolor: 'background.paper',
                                                    minHeight: 32,
                                                    py: '2px !important',
                                                },
                                            }}
                                        />
                                    )}
                                    sx={{ flex: 1, minWidth: 200 }}
                                />
                            </Stack>

                            {/* Schedule */}
                            {availableModes.length > 1 && (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography
                                        variant="overline"
                                        color="text.secondary"
                                        sx={{ fontSize: '0.65rem', letterSpacing: 0.6, lineHeight: 1, whiteSpace: 'nowrap', fontWeight: 600 }}
                                    >
                                        Schedule
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={timingMode}
                                        exclusive
                                        onChange={handleTimingModeChange}
                                        size="small"
                                        sx={{
                                            bgcolor: 'background.paper',
                                            '& .MuiToggleButton-root': {
                                                textTransform: 'none',
                                                px: 1.5,
                                                py: 0.5,
                                                fontSize: '0.75rem',
                                                fontWeight: 500,
                                                border: '1px solid',
                                                borderColor: 'divider',
                                            },
                                        }}
                                    >
                                        {availableModes.map((mode) => {
                                            const { label, icon, desc } = TIMING_MODE_LABELS[mode];
                                            return (
                                                <ToggleButton key={mode} value={mode}>
                                                    <Tooltip title={desc}>
                                                        <Stack direction="row" alignItems="center" spacing={0.75}>
                                                            <Iconify icon={icon} sx={{ width: 16, height: 16 }} />
                                                            <span>{label}</span>
                                                        </Stack>
                                                    </Tooltip>
                                                </ToggleButton>
                                            );
                                        })}
                                    </ToggleButtonGroup>
                                </Stack>
                            )}

                            {/* Shift */}
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.65rem', letterSpacing: 0.6, lineHeight: 1, whiteSpace: 'nowrap', fontWeight: 600 }}
                                >
                                    Move dates
                                </Typography>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    {[-7, -3, -1, 0, 1, 3, 7].map((d) => (
                                        <Chip
                                            key={d}
                                            label={d === 0 ? 'Today' : `${d > 0 ? '+' : ''}${d}d`}
                                            size="small"
                                            variant={dayOffset === d ? 'filled' : 'outlined'}
                                            color={dayOffset === d ? 'primary' : 'default'}
                                            onClick={() => handleDayOffsetChange(d)}
                                            sx={{
                                                cursor: 'pointer',
                                                minWidth: 42,
                                                height: 26,
                                                fontSize: '0.7rem',
                                                fontWeight: 500,
                                                bgcolor: dayOffset === d ? 'primary.main' : 'background.paper',
                                            }}
                                        />
                                    ))}
                                    {dayOffset !== null && (
                                        <Chip
                                            label="Clear"
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            onDelete={() => handleDayOffsetChange(dayOffset)}
                                            sx={{ height: 26, fontSize: '0.7rem' }}
                                        />
                                    )}
                                </Stack>
                            </Stack>

                            {/* Per day */}
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.65rem', letterSpacing: 0.6, lineHeight: 1, whiteSpace: 'nowrap', fontWeight: 600 }}
                                >
                                    Max per day
                                </Typography>
                                <TextField
                                    type="number"
                                    size="small"
                                    value={leadsPerDay}
                                    onChange={(e) => handleLeadsPerDayChange(e.target.value)}
                                    inputProps={{ min: 1, max: 50, style: { width: 36, textAlign: 'center', fontSize: '0.8rem', padding: '4px 6px', fontWeight: 600 } }}
                                    variant="outlined"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            height: 28,
                                            bgcolor: 'background.paper',
                                        },
                                    }}
                                />
                            </Stack>
                        </Stack>
                    </Box>
                )}

                {/* Loading */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                )}

                {/* Error */}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Preview table */}
                {!loading && !error && previewRows.length > 0 && (
                    <TableContainer sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={allChecked}
                                            indeterminate={someChecked}
                                            onChange={(e) => toggleAll(e.target.checked)}
                                        />
                                    </TableCell>
                                    <TableCell>Lead</TableCell>
                                    <TableCell align="center">Count</TableCell>
                                    <TableCell>
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                            <span>Last Contact</span>
                                            <Tooltip title="Sorted by oldest first. Coloured band shows recency: green ≤7d, amber 8–30d, red 30d+ or never.">
                                                <Box sx={{ display: 'inline-flex' }}>
                                                    <Iconify icon="mdi:sort-clock-descending-outline" sx={{ width: 14, height: 14, color: 'text.disabled' }} />
                                                </Box>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="center">Method</TableCell>
                                    <TableCell>Due Date</TableCell>
                                    <TableCell>Assignee</TableCell>
                                    <TableCell align="center">Warning</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayedRows.map((row) => (
                                    <TableRow
                                        key={row.leadId}
                                        hover={!row.isArchiveCandidate}
                                        sx={{
                                            opacity: (() => {
                                                if (row.isArchiveCandidate) return 0.45;
                                                return row.checked ? 1 : 0.5;
                                            })(),
                                            cursor: row.isArchiveCandidate ? 'default' : 'pointer',
                                            bgcolor: row.isArchiveCandidate ? 'action.hover' : 'inherit',
                                            borderLeft: '3px solid',
                                            borderLeftColor: recencyBorderColor(row),
                                            '& > td:first-of-type': { pl: 1 },
                                        }}
                                        onClick={() => !row.isArchiveCandidate && toggleRow(row.leadId)}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={row.checked}
                                                disabled={row.isArchiveCandidate}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                <Typography variant="subtitle2" noWrap>
                                                    {row.firstName} {row.lastName}
                                                </Typography>
                                                {row.isArchiveCandidate && (
                                                    <Tooltip title="Archive candidate — contacted 5+ times">
                                                        <Box><Iconify icon="mdi:archive-outline" sx={{ width: 16, height: 16, color: 'text.disabled' }} /></Box>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={row.contactedCount}
                                                size="small"
                                                color={row.contactedCount === 0 ? 'error' : 'default'}
                                                variant={row.contactedCount === 0 ? 'filled' : 'outlined'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Stack spacing={0}>
                                                {row.latestNoteDate && row.latestNoteDate.isValid() ? (
                                                    <Box sx={{ fontSize: '0.7rem', lineHeight: 1.3 }}>
                                                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                            {row.latestNoteDate.format('DD MMM YYYY')}
                                                        </Box>
                                                        {' '}
                                                        <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400 }}>
                                                            ({row.latestNoteDate.fromNow()})
                                                        </Box>
                                                        {row.latestContactedVia && (
                                                            <Box component="span" sx={{ ml: 0.5, color: 'text.secondary', fontWeight: 400 }}>
                                                                via {row.latestContactedVia?.replace('_', ' ').toLowerCase()}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                ) : row.consultationAttendedDate && row.consultationAttendedDate.isValid() ? (
                                                    <Box sx={{ fontSize: '0.7rem', fontWeight: 600, color: 'text.secondary', lineHeight: 1.3 }}>
                                                        Cons: {row.consultationAttendedDate.format('DD MMM YYYY')}
                                                    </Box>
                                                ) : row.contactedCount > 0 ? (
                                                    <Box sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.3 }}>
                                                        Contacted {row.contactedCount}x
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ fontSize: '0.7rem', color: 'text.disabled', lineHeight: 1.3 }}>
                                                        No contact
                                                    </Box>
                                                )}
                                                {row.latestNote && (
                                                    <Box
                                                        sx={{
                                                            maxWidth: 220,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            fontSize: '0.7rem',
                                                            color: 'text.secondary',
                                                            fontStyle: 'italic',
                                                            lineHeight: 1.3,
                                                            mt: 0.25,
                                                        }}
                                                    >
                                                        &ldquo;{row.latestNote.substring(0, 60)}{row.latestNote.length > 60 ? '…' : ''}&rdquo;
                                                    </Box>
                                                )}
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="center">
                                            {row.isArchiveCandidate ? (
                                                <Typography variant="caption" color="text.disabled">—</Typography>
                                            ) : (
                                                <Tooltip title={row.method}>
                                                    <Box>
                                                        <Iconify
                                                            icon={METHOD_ICONS[row.method] || METHOD_ICONS.OTHER}
                                                            sx={{
                                                                color: METHOD_COLORS[row.method] || METHOD_COLORS.OTHER,
                                                                width: 20,
                                                                height: 20,
                                                            }}
                                                        />
                                                    </Box>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            {row.isArchiveCandidate ? (
                                                <Chip label="Archive" size="small" variant="outlined" color="default"
                                                    icon={<Iconify icon="mdi:archive-outline" />}
                                                    sx={{ fontSize: '0.7rem' }}
                                                />
                                            ) : (
                                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                                    <DateTimePicker
                                                        value={row.dueDate?.toDate() || null}
                                                        onChange={(val) => val && updateRowDate(row.leadId, moment(val))}
                                                        format="EEE dd MMM 'at' h:mm a"
                                                        ampm
                                                        slotProps={{
                                                            textField: {
                                                                size: 'small',
                                                                variant: 'standard',
                                                                sx: {
                                                                    width: 200,
                                                                    '& .MuiInput-underline:before': { borderBottom: 'none' },
                                                                    '& .MuiInput-underline:hover:before': { borderBottom: '1px solid', borderColor: 'primary.main' },
                                                                    '& .MuiInputBase-input': {
                                                                        fontSize: '0.8rem',
                                                                        cursor: 'pointer',
                                                                        ...(row.isOverdue ? { color: 'error.main', fontWeight: 600 } : {}),
                                                                    },
                                                                },
                                                                InputProps: { disableUnderline: false },
                                                            },
                                                        }}
                                                    />
                                                    {row.isOverdue && (
                                                        <Chip label="overdue" size="small" color="error" variant="outlined" sx={{ fontSize: '0.6rem', height: 18 }} />
                                                    )}
                                                </Stack>
                                            )}
                                        </TableCell>
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            <Autocomplete
                                                multiple
                                                size="small"
                                                disabled={row.isArchiveCandidate}
                                                options={userOptions}
                                                value={resolveAssignees(row)}
                                                onChange={(_, newVal) => setPerRowAssignees((prev) => ({ ...prev, [row.leadId]: newVal }))}
                                                getOptionLabel={(opt) => `${opt.user?.first_name || ''} ${opt.user?.last_name || ''}`.trim()}
                                                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                                                renderTags={(selected, getTagProps) =>
                                                    selected.slice(0, 2).map((opt, index) => {
                                                        const fullName = `${opt.user?.first_name || ''} ${opt.user?.last_name || ''}`.trim();
                                                        // Tooltip lives INSIDE the Chip via the avatar slot so it
                                                        // doesn't fight Autocomplete's getTagProps for the chip ref —
                                                        // ref-fight breaks Backspace/delete-icon removal.
                                                        return (
                                                            <Chip
                                                                {...getTagProps({ index })}
                                                                key={opt.id}
                                                                size="small"
                                                                label=""
                                                                avatar={
                                                                    <Tooltip title={fullName}>
                                                                        <AssignUserAvatar
                                                                            name={fullName}
                                                                            src={opt.user?.avatar || ''}
                                                                            role={opt.role}
                                                                            sx={{ width: 22, height: 22, fontSize: '0.65rem' }}
                                                                        />
                                                                    </Tooltip>
                                                                }
                                                                sx={{
                                                                    height: 26,
                                                                    pl: 0,
                                                                    pr: 0,
                                                                    bgcolor: 'transparent',
                                                                    border: 'none',
                                                                    '& .MuiChip-label': { p: 0 },
                                                                    '& .MuiChip-deleteIcon': { display: 'none' },
                                                                    '&:hover .MuiChip-deleteIcon': { display: 'inline-flex' },
                                                                }}
                                                            />
                                                        );
                                                    })
                                                }
                                                renderOption={(props, opt) => (
                                                    <li {...props} key={opt.id}>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <AssignUserAvatar
                                                                name={`${opt.user?.first_name || ''} ${opt.user?.last_name || ''}`}
                                                                src={opt.user?.avatar || ''}
                                                                role={opt.role}
                                                                sx={{ width: 22, height: 22, fontSize: '0.65rem' }}
                                                            />
                                                            <Typography variant="body2">{opt.user?.first_name} {opt.user?.last_name}</Typography>
                                                        </Stack>
                                                    </li>
                                                )}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        variant="standard"
                                                        placeholder={resolveAssignees(row).length === 0 ? 'Assign…' : ''}
                                                        sx={{
                                                            minWidth: 90,
                                                            '& .MuiInput-underline:before': { borderBottom: 'none' },
                                                            '& .MuiInput-underline:hover:before': { borderBottom: '1px solid', borderColor: 'primary.main' },
                                                            '& .MuiInputBase-root': { fontSize: '0.75rem' },
                                                        }}
                                                    />
                                                )}
                                                sx={{ width: 140 }}
                                            />
                                            {resolveAssignees(row).length > 2 && (
                                                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                                    +{resolveAssignees(row).length - 2}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={0.5} justifyContent="center">
                                                {row.pendingReminderCount > 0 && (
                                                    <Tooltip
                                                        title={`${row.pendingReminderCount} pending reminder${row.pendingReminderCount > 1 ? 's' : ''}${
                                                            row.nextReminderDatetime?.isValid()
                                                                ? ` · Next: ${row.nextReminderDatetime.format('DD MMM YYYY')}`
                                                                : ''
                                                        }`}
                                                    >
                                                        <Box>
                                                            <Iconify
                                                                icon="mdi:bell-alert"
                                                                sx={{ color: 'error.main', width: 20, height: 20 }}
                                                            />
                                                        </Box>
                                                    </Tooltip>
                                                )}
                                                {row.hasUpcomingAppointment && (
                                                    <Tooltip
                                                        title={`Appointment: ${
                                                            row.nearestAppointmentDatetime?.isValid()
                                                                ? row.nearestAppointmentDatetime.format('DD MMM YYYY')
                                                                : 'upcoming'
                                                        }`}
                                                    >
                                                        <Box>
                                                            <Iconify
                                                                icon="mdi:calendar-alert"
                                                                sx={{ color: 'warning.main', width: 20, height: 20 }}
                                                            />
                                                        </Box>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Stack>

            {/* Footer */}
            <Box sx={reminderFooterStyle}>
                <Button color="inherit" sx={cancelButtonStyle} onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleCreate}
                    disabled={checkedRows.filter((r) => r.dueDate).length === 0 || loading || creating || isBlocked}
                    startIcon={<Iconify icon="mdi:bell-plus" />}
                    sx={{
                        ...saveButtonStyle,
                        bgcolor: 'success.main',
                        '&:hover': { bgcolor: 'success.dark' },
                    }}
                >
                    {creating
                        ? 'Creating...'
                        : `Create ${checkedRows.filter((r) => r.dueDate).length} Reminder${checkedRows.filter((r) => r.dueDate).length !== 1 ? 's' : ''}`}
                </Button>
            </Box>
        </Dialog>
    );
};

export default SmartBulkReminderModal;

SmartBulkReminderModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onSuccess: PropTypes.func,
};
